import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import {
  AgrupamentoContasAPagar,
  RelatorioContasAPagarAgrupado,
  RelatorioContasAPagarGrupo,
  RelatorioContasAPagarItem,
  TipoDataContasAPagar
} from '../../../models/financeiro.model';
import { formatarNumeroPtBR } from '../../../core/utils/moeda.util';

@Component({
  selector: 'app-relatorio-contas-a-pagar-agrupado',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-contas-a-pagar-agrupado.component.html',
  styleUrl: './relatorio-contas-a-pagar-agrupado.component.css'
})
export class RelatorioContasAPagarAgrupadoComponent {
  private analyticsService = inject(AnalyticsService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);

  tipoData: TipoDataContasAPagar = 'VENCIMENTO';
  agrupamento: AgrupamentoContasAPagar = 'GRUPO_SUBGRUPO';
  status = '';
  dataInicial = '';
  dataFinal = '';

  relatorio: RelatorioContasAPagarAgrupado | null = null;
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  /** Grupos e subgrupos recolhidos, indexados por chave estável. */
  private recolhidos = new Set<string>();

  get dataInicialInvalida(): boolean {
    return this.tentouBuscar && this.dataInicial.trim() === '';
  }

  get dataFinalInvalida(): boolean {
    return this.tentouBuscar && this.dataFinal.trim() === '';
  }

  get agrupaPorSubgrupo(): boolean {
    return this.agrupamento === 'GRUPO_SUBGRUPO';
  }

  get grupos(): RelatorioContasAPagarGrupo[] {
    return this.relatorio?.grupos ?? [];
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.dataInicial.trim() === '' || this.dataFinal.trim() === '') {
      this.toastr.warning('Informe a data inicial e a data final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    if (this.dataInicial > this.dataFinal) {
      this.toastr.warning('A data inicial deve ser anterior ou igual à data final.', 'Período inválido');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    this.analyticsService.getRelatorioContasAPagarAgrupado(
      this.tipoData,
      this.dataInicial,
      this.dataFinal,
      this.agrupamento,
      this.status || undefined
    ).subscribe({
      next: (data) => {
        this.relatorio = data;
        this.recolhidos.clear();
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        const mensagem = error?.error?.message || 'Erro ao carregar o relatório de contas a pagar.';
        this.toastr.error(mensagem, 'Erro');
        this.carregando = false;
        this.cdr.detectChanges();
      }
    });
  }

  limpar(): void {
    this.tipoData = 'VENCIMENTO';
    this.agrupamento = 'GRUPO_SUBGRUPO';
    this.status = '';
    this.dataInicial = '';
    this.dataFinal = '';
    this.relatorio = null;
    this.carregado = false;
    this.tentouBuscar = false;
    this.recolhidos.clear();
    this.cdr.detectChanges();
  }

  // ----- Recolher / expandir -----

  chaveGrupo(grupo: RelatorioContasAPagarGrupo): string {
    return `g:${grupo.grupoId ?? grupo.grupo}`;
  }

  chaveSubgrupo(grupo: RelatorioContasAPagarGrupo, subgrupoId: number | null, subgrupo: string): string {
    return `${this.chaveGrupo(grupo)}|s:${subgrupoId ?? subgrupo}`;
  }

  estaRecolhido(chave: string): boolean {
    return this.recolhidos.has(chave);
  }

  alternar(chave: string): void {
    if (this.recolhidos.has(chave)) {
      this.recolhidos.delete(chave);
    } else {
      this.recolhidos.add(chave);
    }
    this.cdr.detectChanges();
  }

  // ----- Apresentação -----

  formatDate(value: string | undefined | null): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? value;
  }

  get periodoLabel(): string {
    const rotulo = this.tipoData === 'VENCIMENTO' ? 'Vencimento' : 'Pagamento';
    return `${rotulo}: ${this.formatDate(this.dataInicial)} a ${this.formatDate(this.dataFinal)}`;
  }

  get statusLabel(): string {
    return this.status || 'Todos';
  }

  percentual(valor: number): number {
    const total = this.relatorio?.valorTotalPrevisto ?? 0;
    if (!total) {
      return 0;
    }
    return (valor / total) * 100;
  }

  imprimir(): void {
    if (!this.relatorio) {
      return;
    }

    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const num = (value: number | null | undefined) => formatarNumeroPtBR(value ?? 0);

    const linhasItens = (itens: RelatorioContasAPagarItem[], recuo: number): string =>
      itens.map((item) => `
        <tr class="item">
          <td style="padding-left:${recuo}mm">${esc(item.fornecedor)}</td>
          <td>${esc(item.descricao)}</td>
          <td class="center">${esc(this.formatDate(item.dataVencimento))}</td>
          <td class="center">${esc(this.formatDate(item.dataPagamento))}</td>
          <td class="center">${esc(item.status)}</td>
          <td class="right">${num(item.valorPrevisto)}</td>
          <td class="right">${num(item.valorPago)}</td>
        </tr>`).join('');

    const corpo = this.relatorio.grupos.map((grupo) => {
      const cabecalhoGrupo = `
        <tr class="grupo">
          <td colspan="5">${esc(grupo.grupo)} &nbsp;(${esc(grupo.totalRegistros)} conta(s))</td>
          <td class="right">${num(grupo.valorTotalPrevisto)}</td>
          <td class="right">${num(grupo.valorTotalPago)}</td>
        </tr>`;

      if (!this.agrupaPorSubgrupo) {
        return cabecalhoGrupo + linhasItens(grupo.itens, 6);
      }

      const blocosSubgrupo = grupo.subgrupos.map((sub) => `
        <tr class="subgrupo">
          <td colspan="5" style="padding-left:6mm">${esc(sub.subgrupo)} &nbsp;(${esc(sub.totalRegistros)} conta(s))</td>
          <td class="right">${num(sub.valorTotalPrevisto)}</td>
          <td class="right">${num(sub.valorTotalPago)}</td>
        </tr>` + linhasItens(sub.itens, 12)).join('');

      return cabecalhoGrupo + blocosSubgrupo;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Contas a Pagar Agrupado</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }

    @page {
      size: A4 landscape;
      margin: 14mm 12mm 18mm 12mm;
      @bottom-left   { content: "Relatório de Contas a Pagar Agrupado"; font-size: 7.5pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7.5pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${esc(this.dataGeracao)}"; font-size: 7.5pt; color: #555; }
    }

    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 0.5px; }
    .header .meta { font-size: 9pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 8px; }

    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 6px 7px; font-size: 9pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 4px 7px; font-size: 9pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    tbody tr.grupo td { background-color: #dbeafe; font-weight: 700; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr.subgrupo td { background-color: #eff6ff; font-weight: 600; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr.item td { background-color: #fff; }

    tfoot tr td { padding: 5px 7px; font-size: 9pt; font-weight: 700; border-top: 2px solid #000; border-bottom: 1px solid #ccc; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    .center { text-align: center; }
    .right  { text-align: right; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Contas a Pagar Agrupado</h1>
    <div class="meta">
      ${esc(this.periodoLabel)} &nbsp;|&nbsp; Status: ${esc(this.statusLabel)}
      &nbsp;|&nbsp; Agrupamento: ${esc(this.agrupaPorSubgrupo ? 'Grupo e Subgrupo' : 'Somente Grupo')}
      &nbsp;|&nbsp; Total: ${esc(this.relatorio.totalRegistros)} conta(s)
      &nbsp;|&nbsp; Gerado em: ${esc(this.dataGeracao)}
    </div>
    <hr class="divider"/>
  </div>

  <table>
    <thead>
      <tr>
        <th>Grupo / Subgrupo / Fornecedor</th>
        <th>Descrição</th>
        <th class="center">Vencimento</th>
        <th class="center">Pagamento</th>
        <th class="center">Status</th>
        <th class="right">Valor Previsto</th>
        <th class="right">Valor Pago</th>
      </tr>
    </thead>
    <tbody>${corpo}</tbody>
    <tfoot>
      <tr>
        <td colspan="5" class="right">Total geral:</td>
        <td class="right">${num(this.relatorio.valorTotalPrevisto)}</td>
        <td class="right">${num(this.relatorio.valorTotalPago)}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    if (!win) {
      this.toastr.warning('O navegador bloqueou a abertura da janela de impressão. Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }
}
